import React, { useState, useEffect } from 'react';
import { CurrencyDollar, Medal, TrendUp } from '@phosphor-icons/react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import type { EmployeeDetails } from '../../../stores/employeeProfileStore';

interface BenefitsSectionProps {
    employee: EmployeeDetails;
}

interface CompensationData {
    user_uuid: string;
    currency: string;
    base_salary: string;
    bonus_percentage: string;
    bonus_amount: string;
    equity: string;
    pay_rate: string;
}

const formatCurrency = (value: string | null | undefined, currency: string = 'USD'): string => {
    if (!value) return '-';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numValue);
};

const formatPercent = (value: string | null | undefined): string => {
    if (!value) return '-';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '-';
    return `${numValue.toFixed(2)}%`;
};

export const BenefitsSection: React.FC<BenefitsSectionProps> = ({ employee }) => {
    const [compData, setCompData] = useState<CompensationData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchCompensationData = async () => {
            if (!employee.user_uuid) {
                setError('No user UUID available');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const token = localStorage.getItem('access_token') ||
                    localStorage.getItem('authToken') ||
                    localStorage.getItem('token');

                const headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                const API_BASE_URL = import.meta.env.VITE_ADMIN_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
                const response = await fetch(
                    `${API_BASE_URL}/admin/comp-users/employees/${employee.user_uuid}`,
                    {
                        method: 'GET',
                        headers,
                        credentials: 'include',
                    }
                );

                if (!response.ok) {
                    if (response.status === 404) {
                        setCompData(null);
                        setLoading(false);
                        return;
                    }
                    throw new Error(`Failed to fetch compensation data: ${response.status}`);
                }

                const result = await response.json();
                if (result.data) {
                    setCompData(result.data);
                } else {
                    setCompData(null);
                }
            } catch (err: any) {
                console.error('Failed to fetch compensation data:', err);
                setError(err.message || 'Failed to load compensation data');
            } finally {
                setLoading(false);
            }
        };

        fetchCompensationData();
    }, [employee.user_uuid]);

    if (loading) {
        return (
            <div className="w-full flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full">
                <div className="text-sm text-slate-500">{error}</div>
            </div>
        );
    }

    if (!compData) {
        return (
            <div className="w-full text-center py-12">
                <CurrencyDollar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No compensation data available.</p>
            </div>
        );
    }

    const currency = compData.currency || 'USD';

    const baseSalary = parseFloat(compData.base_salary) || 0;
    const bonusAmount = parseFloat(compData.bonus_amount) || 0;
    const equity = parseFloat(compData.equity) || 0;
    const totalComp = baseSalary + bonusAmount + equity;

    const formatCurrencyCompact = (v: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency, notation: 'compact' }).format(v);
    const formatCurrencyFull = (v: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

    const compBreakdownData = [
        { name: 'Base Salary', value: baseSalary, color: '#3b82f6' },
        { name: 'Bonus', value: bonusAmount, color: '#f59e0b' },
        { name: 'Equity', value: equity, color: '#8b5cf6' },
    ].filter(d => d.value > 0);

    return (
        <div className="w-full">
            {/* Read-only fields */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 w-full">
                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Currency</label>
                    <div className="text-sm font-medium text-slate-900">{compData.currency || '-'}</div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Base Salary</label>
                    <div className="text-sm font-medium text-slate-900">{formatCurrency(compData.base_salary, currency)}</div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Bonus Amount</label>
                    <div className="text-sm font-medium text-slate-900">{formatCurrency(compData.bonus_amount, currency)}</div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Bonus Percentage</label>
                    <div className="text-sm font-medium text-slate-900">{formatPercent(compData.bonus_percentage)}</div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Equity (Shares)</label>
                    <div className="text-sm font-medium text-slate-900">
                        {compData.equity ? parseFloat(compData.equity).toLocaleString() : '-'}
                    </div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Pay Rate</label>
                    <div className="text-sm font-medium text-slate-900">
                        {compData.pay_rate ? `${formatCurrency(compData.pay_rate, currency)}/hr` : '-'}
                    </div>
                </div>
            </div>

            {/* Charts */}
            {totalComp > 0 && (
                <div className="mt-8 space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="rounded-xl p-4 border shadow-sm" style={{ borderColor: 'rgba(59,130,246,0.28)', backgroundImage: 'linear-gradient(135deg, rgba(59,130,246,0.10) 0%, #ffffff 72%)' }}>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(59,130,246,0.14)', color: '#3b82f6' }}>
                                    <CurrencyDollar className="h-4 w-4" />
                                </div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Base Salary</p>
                            </div>
                            <p className="text-xl font-bold text-slate-900">{formatCurrencyFull(baseSalary)}</p>
                            <p className="text-xs text-slate-400 mt-0.5">Annual</p>
                        </div>

                        <div className="rounded-xl p-4 border shadow-sm" style={{ borderColor: 'rgba(245,158,11,0.28)', backgroundImage: 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, #ffffff 72%)' }}>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(245,158,11,0.14)', color: '#f59e0b' }}>
                                    <TrendUp className="h-4 w-4" />
                                </div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-amber-500">Bonus</p>
                            </div>
                            <p className="text-xl font-bold text-slate-900">{formatCurrencyFull(bonusAmount)}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{formatPercent(compData.bonus_percentage)} of base</p>
                        </div>

                        <div className="rounded-xl p-4 border shadow-sm" style={{ borderColor: 'rgba(139,92,246,0.28)', backgroundImage: 'linear-gradient(135deg, rgba(139,92,246,0.10) 0%, #ffffff 72%)' }}>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(139,92,246,0.14)', color: '#8b5cf6' }}>
                                    <Medal className="h-4 w-4" />
                                </div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">Equity</p>
                            </div>
                            <p className="text-xl font-bold text-slate-900">{equity > 0 ? parseFloat(compData.equity).toLocaleString() : '-'}</p>
                            <p className="text-xs text-slate-400 mt-0.5">Shares</p>
                        </div>
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-2 gap-6">
                        {/* Bar Chart */}
                        <div className="border rounded-xl p-4">
                            <h4 className="text-sm font-semibold text-slate-700 mb-3">Compensation Breakdown</h4>
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={compBreakdownData} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis type="number" tickFormatter={(v) => formatCurrencyCompact(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} width={80} />
                                    <RechartsTooltip formatter={(value: number) => formatCurrencyFull(value)} />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                        {compBreakdownData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Pie Chart */}
                        <div className="border rounded-xl p-4">
                            <h4 className="text-sm font-semibold text-slate-700 mb-3">Compensation Split</h4>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie data={compBreakdownData} cx="50%" cy="50%" outerRadius={70} innerRadius={40} dataKey="value" paddingAngle={2}>
                                        {compBreakdownData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip formatter={(value: number) => formatCurrencyFull(value)} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="flex justify-center gap-4 mt-2">
                                {compBreakdownData.map((entry) => (
                                    <div key={entry.name} className="flex items-center gap-1.5">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                                        <span className="text-xs text-slate-600">{entry.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BenefitsSection;
